import { IActivityEntry, IExerciseInvite } from "@hulanbv/platformapp";
import { ModelQuery } from "nest-utilities-client";
import { FC, useCallback } from "react";
import { generatePath, useHistory } from "react-router";
import { useUrlState } from "../../../../state/common/hooks/url-state-hook/use-url-state.hook";
import { ReactComponent as eyeIcon } from "../../../../assets/graphics/symbols/eye.svg";
import { urlUtils } from "../../../../utils/url.utils";
import { Table } from "../../../elements/table.element";
import { dictionary } from "../../../../state/common/constants/dictionary.constants";
import { TableHeadTemplate } from "../../table-head.template";
import { exerciseInviteService } from "../../../../state/exercise-invite/exercise-invite.service";
import { QuestionnaireRowTemplate } from "../../table-rows/questionnaire-row.template";
import { routes } from "../../../../state/common/constants/routes.constants";
import { activityEntriesService } from "../../../../state/activities/activity-entries.service";
import { TableRow } from "../../../elements/table-row.element";
import { TableRowContent } from "../../../elements/table-row-content.element";
import { IconButton } from "../../../elements/icon-button.element";
import { dateUtils } from "../../../../utils/date.utils";
import { activityTypeLabels } from "../../../../state/activities/activity-type-labels";

interface IProps {
  clientId: string;
  title?: string;
  match?: ModelQuery<IExerciseInvite>;
}

export const ClientQuestionnairesAndActivitiesTable: FC<IProps> = (
  props: IProps,
) => {
  const [searchParamValue, setSearchParamValue] =
    useUrlState<string>("search-filter");
  const history = useHistory();
  const [, setEarliestCreatedAt] = useUrlState<string>("earliest-created-at");

  const rowTemplate = useCallback(
    (row: IExerciseInvite | IActivityEntry) => {
      if ("sessionIdentifier" in row) {
        // It's an activity entry
        return (
          <TableRow>
            <TableRowContent
              header={activityTypeLabels[row.activityType]}
              subHeader={[
                row.exerciseInvite?.exercise?.name,
                `${dictionary.literals.completedOn} ${dateUtils.getFormattedDate(
                  row.createdAt ?? new Date(),
                )}`,
              ]
                .filter(Boolean)
                .join(" · ")}
              subHeaderSize="tiny"
              controls={
                <IconButton
                  icon={eyeIcon}
                  attributes={{
                    onClick: () => {
                      history.push(
                        generatePath(routes.activityResults.path, {
                          userId: row.userId,
                          exerciseInviteId: row.exerciseInviteId ?? "",
                          sessionId: row.sessionIdentifier ?? "",
                        }),
                      );
                    },
                  }}
                />
              }
            />
          </TableRow>
        );
      }

      const questionnaire = row.exercise?.questionnaire;
      if (!questionnaire) {
        return <></>;
      }

      return (
        <QuestionnaireRowTemplate
          questionnaire={questionnaire}
          inviteDate={row.createdAt}
          key={questionnaire._id + row._id}
          onViewClick={() => {
            const path = generatePath(routes.questionnaireResults.path, {
              questionnaireId: questionnaire.id,
              userId: row.inviteeId,
              exerciseInviteId: row.id,
            });
            history.push(path);
          }}
        />
      );
    },
    [history],
  );

  const fetchData = useCallback(
    async (skip: number, limit: number) => {
      // get search param and creation date from query param to preserve across fetches
      const searchParams = new URLSearchParams(window.location.search);
      const searchParamValue = searchParams.get("search-filter") ?? "";
      const earliestCreatedAt = searchParams.get("earliest-created-at") ?? "";

      const { data: exercises } = await exerciseInviteService.getAll({
        match: {
          $and: [
            {
              inviteeId: props.clientId,
              "exercise.questionnaireId": { $exists: true },
              ...(searchParamValue &&
                urlUtils.createSearchQuery(searchParamValue, [
                  "exercise.questionnaire.title",
                ])),
            },
            ...(earliestCreatedAt
              ? [
                  {
                    scheduledDate: {
                      $lt: new Date(earliestCreatedAt).toISOString(),
                    },
                  },
                ]
              : []),
          ],
          ...(props.match ?? {}),
        },
        populate: ["exercise.questionnaire"],
        sort: ["-scheduledDate"],
        limit,
      });

      const earliestExerciseCreatedAt = Math.min(
        ...exercises.map((invite) =>
          new Date(invite.scheduledDate ?? 0).getTime(),
        ),
      );
      const latestExerciseCreatedAt = Math.max(
        ...exercises.map((invite) =>
          new Date(invite.scheduledDate ?? 0).getTime(),
        ),
      );
      const { data: activityEntries } = await activityEntriesService.getAll({
        match: {
          userId: props.clientId,
          createdAt: {
            ...(exercises.length < limit
              ? {}
              : { $gte: new Date(earliestExerciseCreatedAt).toISOString() }),
            ...(skip === 0
              ? {}
              : { $lte: new Date(latestExerciseCreatedAt).toISOString() }),
          },
        },
        distinct: "sessionIdentifier",
        populate: [
          {
            path: "exerciseInvite",
            populate: [{ path: "exercise", select: ["name"] }],
          },
        ],
      });

      setEarliestCreatedAt(
        exercises.length > 0
          ? new Date(
              Math.min(
                earliestExerciseCreatedAt,
                ...activityEntries.map((entry) =>
                  new Date(entry.createdAt ?? 0).getTime(),
                ),
              ),
            ).toISOString()
          : "",
      );

      return [...exercises, ...activityEntries].sort((a, b) => {
        const dateA = new Date(a.createdAt ?? 0).getTime();
        const dateB = new Date(b.createdAt ?? 0).getTime();
        return dateB - dateA;
      });
    },
    [props.clientId, props.match, setEarliestCreatedAt],
  );

  return (
    <div>
      <TableHeadTemplate
        header={props.title ?? dictionary.literals.answers}
        headerAttributes={{ center: false }}
        searchInputAttributes={{
          defaultValue: searchParamValue ?? "",
          onChange: (event) => setSearchParamValue(event.target.value),
        }}
      />
      <Table<IExerciseInvite | IActivityEntry>
        rowTemplate={rowTemplate}
        fetchRequest={fetchData}
      />
    </div>
  );
};
